import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FaTimes, FaHome, FaUser, FaDollarSign, FaUsers, FaPlus } from 'react-icons/fa';
import { createAdminRental, fetchAdminRentalDashboard } from '../../../Redux/Actions/actions';
import style from './NewAdminRentalContract.module.css';

const emptyTenant = () => ({
  fullName: '',
  dni: '',
  cuit: '',
  email: '',
  phone: '',
  tenantType: 'main',
  occupation: '',
  employer: '',
  monthlyIncome: '',
  previousAddress: '',
  notes: '',
});

const initialFormData = {
  // Propiedad embebida
  propertyAddress: '',
  propertyDepartment: '',
  propertyProvince: '',
  propertyCountry: 'Argentina',
  propertyType: '',
  propertyReference: '',
  propertyNotes: '',
  // Referencia interna (opcional)
  sellerId: '',
  officeId: '',
  // Propietario
  ownerName: '',
  ownerDni: '',
  ownerCuit: '',
  ownerPhone: '',
  ownerEmail: '',
  ownerBankAccount: '',
  ownerBankAlias: '',
  // Canon
  rentValue: '',
  currency: 'ARS',
  adminFeePercent: 10,
  // Contrato
  contractStart: '',
  contractEnd: '',
  paymentDueDay: 10,
  // Ajustes
  adjustmentType: 'ICL',
  adjustmentFrequency: 12,
  adjustmentPercentage: '',
  // Expensas / servicios
  expensesIncluded: false,
  expensesAmount: 0,
  servicesIncluded: {
    agua: false,
    gas: false,
    luz: false,
    internet: false,
    abl: false,
  },
  // Mora
  lateInterestRate: 0,
  gracePeriodDays: 0,
  // Notas / cláusulas
  notes: '',
  specialClauses: '',
};

const calculateContractEnd = (startDate, months = 24) => {
  if (!startDate) return '';
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return '';
  const end = new Date(start);
  end.setMonth(end.getMonth() + months);
  return end.toISOString().split('T')[0];
};

const NewAdminRentalContract = ({ onClose }) => {
  const dispatch = useDispatch();
  const adminRentalsLoading = useSelector((s) => s.adminRentalsLoading);

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [tenants, setTenants] = useState([emptyTenant()]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleServiceChange = (key) => {
    setFormData((prev) => ({
      ...prev,
      servicesIncluded: {
        ...prev.servicesIncluded,
        [key]: !prev.servicesIncluded[key],
      },
    }));
  };

  const handleStartChange = (e) => {
    const startDate = e.target.value;
    setFormData((prev) => ({
      ...prev,
      contractStart: startDate,
      contractEnd: prev.contractEnd || calculateContractEnd(startDate),
    }));
  };

  const handleTenantChange = (idx, field, value) => {
    setTenants((prev) =>
      prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t))
    );
  };

  const addTenant = () => setTenants((prev) => [...prev, emptyTenant()]);
  const removeTenant = (idx) =>
    setTenants((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const validateStep = (step) => {
    setError(null);
    if (step === 1) {
      if (!formData.propertyAddress.trim()) return 'La dirección de la propiedad es obligatoria';
      if (!formData.ownerName.trim()) return 'El nombre del propietario es obligatorio';
    }
    if (step === 2) {
      const rent = Number(formData.rentValue);
      if (!Number.isFinite(rent) || rent <= 0) return 'El canon debe ser mayor a 0';
      if (!formData.contractStart) return 'La fecha de inicio es obligatoria';
      if (!formData.contractEnd) return 'La fecha de fin es obligatoria';
      const dueDay = Number(formData.paymentDueDay);
      if (!Number.isInteger(dueDay) || dueDay < 1 || dueDay > 31)
        return 'El día de vencimiento debe ser entre 1 y 31';
      const fee = Number(formData.adminFeePercent);
      if (
        !Number.isFinite(fee) ||
        !Number.isInteger(fee) ||
        fee < 0 ||
        fee > 100
      )
        return 'El canon de administración debe ser un entero entre 0 y 100';
    }
    if (step === 3) {
      if (!tenants.length) return 'Debe agregar al menos un inquilino';
      for (let i = 0; i < tenants.length; i += 1) {
        const t = tenants[i];
        if (!t.fullName.trim() || !t.dni.trim()) {
          return `Inquilino #${i + 1}: nombre y DNI son obligatorios`;
        }
      }
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep(currentStep);
    if (err) {
      setError(err);
      return;
    }
    setCurrentStep((s) => Math.min(3, s + 1));
  };

  const handlePrev = () => {
    setError(null);
    setCurrentStep((s) => Math.max(1, s - 1));
  };

  const buildPayload = () => {
    const payload = {
      propertyAddress: formData.propertyAddress.trim(),
      propertyDepartment: formData.propertyDepartment.trim() || undefined,
      propertyProvince: formData.propertyProvince.trim() || undefined,
      propertyCountry: formData.propertyCountry.trim() || undefined,
      propertyType: formData.propertyType.trim() || undefined,
      propertyReference: formData.propertyReference.trim() || undefined,
      propertyNotes: formData.propertyNotes.trim() || undefined,
      ownerName: formData.ownerName.trim(),
      ownerDni: formData.ownerDni.trim() || undefined,
      ownerCuit: formData.ownerCuit.trim() || undefined,
      ownerPhone: formData.ownerPhone.trim() || undefined,
      ownerEmail: formData.ownerEmail.trim() || undefined,
      ownerBankAccount: formData.ownerBankAccount.trim() || undefined,
      ownerBankAlias: formData.ownerBankAlias.trim() || undefined,
      rentValue: Number(formData.rentValue),
      currency: formData.currency,
      adminFeePercent: Number(formData.adminFeePercent),
      contractStart: formData.contractStart,
      contractEnd: formData.contractEnd,
      paymentDueDay: Number(formData.paymentDueDay),
      adjustmentType: formData.adjustmentType,
      adjustmentFrequency: Number(formData.adjustmentFrequency) || 12,
      expensesIncluded: !!formData.expensesIncluded,
      expensesAmount: Number(formData.expensesAmount) || 0,
      servicesIncluded: formData.servicesIncluded,
      lateInterestRate: Number(formData.lateInterestRate) || 0,
      gracePeriodDays: Number(formData.gracePeriodDays) || 0,
      notes: formData.notes.trim() || undefined,
      specialClauses: formData.specialClauses.trim() || undefined,
      tenants: tenants.map((t) => ({
        fullName: t.fullName.trim(),
        dni: t.dni.trim(),
        cuit: t.cuit.trim() || undefined,
        email: t.email.trim() || undefined,
        phone: t.phone.trim() || undefined,
        tenantType: t.tenantType || 'main',
        occupation: t.occupation.trim() || undefined,
        employer: t.employer.trim() || undefined,
        monthlyIncome: t.monthlyIncome ? Number(t.monthlyIncome) : undefined,
        previousAddress: t.previousAddress.trim() || undefined,
        notes: t.notes.trim() || undefined,
      })),
    };
    if (formData.adjustmentPercentage !== '')
      payload.adjustmentPercentage = Number(formData.adjustmentPercentage);
    if (formData.sellerId) payload.sellerId = Number(formData.sellerId);
    if (formData.officeId) payload.officeId = Number(formData.officeId);
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validar los 3 steps antes de enviar
    for (let s = 1; s <= 3; s += 1) {
      const err = validateStep(s);
      if (err) {
        setError(err);
        setCurrentStep(s);
        return;
      }
    }
    try {
      const payload = buildPayload();
      await dispatch(createAdminRental(payload));
      toast.success('Administración creada correctamente');
      // Refresca dashboard si el usuario lo tiene abierto detrás
      dispatch(fetchAdminRentalDashboard());
      setFormData(initialFormData);
      setTenants([emptyTenant()]);
      onClose?.();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Error al crear la administración';
      setError(msg);
      toast.error(msg);
    }
  };

  return (
    <div className={style.container}>
      <div className={style.header}>
        <div className={style.headerContent}>
          <h2 className={style.title}>Nueva Administración</h2>
          <div className={style.stepIndicator}>
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`${style.stepDot} ${currentStep === s ? style.active : ''}`}
              >
                {s}
              </div>
            ))}
          </div>
        </div>
        <button type="button" onClick={onClose} className={style.closeButton}>
          <FaTimes />
        </button>
      </div>

      <form onSubmit={handleSubmit} className={style.form}>
        {error && <div className={style.errorBanner}>{error}</div>}

        {currentStep === 1 && (
          <div className={style.step}>
            <h3 className={style.stepTitle}>
              <FaHome /> Propiedad y propietario
            </h3>
            <div className={style.formGrid}>
              <div className={style.formGroup}>
                <label>Dirección *</label>
                <input
                  className={style.input}
                  type="text"
                  name="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={handleInputChange}
                  required
                  placeholder="Av. Corrientes 1234"
                />
              </div>
              <div className={style.formGroup}>
                <label>Departamento / Partido</label>
                <input
                  className={style.input}
                  type="text"
                  name="propertyDepartment"
                  value={formData.propertyDepartment}
                  onChange={handleInputChange}
                  placeholder="Capital Federal"
                />
              </div>
              <div className={style.formGroup}>
                <label>Provincia</label>
                <input
                  className={style.input}
                  type="text"
                  name="propertyProvince"
                  value={formData.propertyProvince}
                  onChange={handleInputChange}
                />
              </div>
              <div className={style.formGroup}>
                <label>País</label>
                <input
                  className={style.input}
                  type="text"
                  name="propertyCountry"
                  value={formData.propertyCountry}
                  onChange={handleInputChange}
                />
              </div>
              <div className={style.formGroup}>
                <label>Tipo de propiedad</label>
                <input
                  className={style.input}
                  type="text"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleInputChange}
                  placeholder="Departamento, Casa, Local..."
                />
              </div>
              <div className={style.formGroup}>
                <label>Referencia (piso, depto, etc.)</label>
                <input
                  className={style.input}
                  type="text"
                  name="propertyReference"
                  value={formData.propertyReference}
                  onChange={handleInputChange}
                  placeholder="Piso 5 Depto B"
                />
              </div>
              <div className={style.formGroup} style={{ gridColumn: '1 / -1' }}>
                <label>Notas de la propiedad</label>
                <textarea
                  className={style.input}
                  name="propertyNotes"
                  value={formData.propertyNotes}
                  onChange={handleInputChange}
                  rows={2}
                />
              </div>
            </div>

            <div className={style.section}>
              <h4 className={style.sectionTitle}>
                <FaUser style={{ marginRight: 6 }} />
                Datos del propietario
              </h4>
              <div className={style.formGrid}>
                <div className={style.formGroup}>
                  <label>Nombre completo *</label>
                  <input
                    className={style.input}
                    type="text"
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className={style.formGroup}>
                  <label>DNI</label>
                  <input
                    className={style.input}
                    type="text"
                    name="ownerDni"
                    value={formData.ownerDni}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={style.formGroup}>
                  <label>CUIT</label>
                  <input
                    className={style.input}
                    type="text"
                    name="ownerCuit"
                    value={formData.ownerCuit}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={style.formGroup}>
                  <label>Teléfono</label>
                  <input
                    className={style.input}
                    type="text"
                    name="ownerPhone"
                    value={formData.ownerPhone}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={style.formGroup}>
                  <label>Email</label>
                  <input
                    className={style.input}
                    type="email"
                    name="ownerEmail"
                    value={formData.ownerEmail}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={style.formGroup}>
                  <label>CBU</label>
                  <input
                    className={style.input}
                    type="text"
                    name="ownerBankAccount"
                    value={formData.ownerBankAccount}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={style.formGroup}>
                  <label>Alias bancario</label>
                  <input
                    className={style.input}
                    type="text"
                    name="ownerBankAlias"
                    value={formData.ownerBankAlias}
                    onChange={handleInputChange}
                    placeholder="juan.perez.mp"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className={style.step}>
            <h3 className={style.stepTitle}>
              <FaDollarSign /> Términos del contrato
            </h3>
            <div className={style.formGrid}>
              <div className={style.formGroup}>
                <label>Canon mensual *</label>
                <div className={style.inputWithCurrency}>
                  <input
                    className={style.input}
                    type="number"
                    name="rentValue"
                    value={formData.rentValue}
                    onChange={handleInputChange}
                    required
                    min="1"
                  />
                  <select
                    className={style.currencySelect}
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                  >
                    <option value="ARS">ARS</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
              <div className={style.formGroup}>
                <label>% Honorarios admin (0-100) *</label>
                <input
                  className={style.input}
                  type="number"
                  name="adminFeePercent"
                  value={formData.adminFeePercent}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="1"
                  required
                />
                <small className={style.helpText}>Entero entre 0 y 100.</small>
              </div>
              <div className={style.formGroup}>
                <label>Día de vencimiento (1-31) *</label>
                <input
                  className={style.input}
                  type="number"
                  name="paymentDueDay"
                  value={formData.paymentDueDay}
                  onChange={handleInputChange}
                  min="1"
                  max="31"
                  required
                />
              </div>
              <div className={style.formGroup}>
                <label>Inicio del contrato *</label>
                <input
                  className={style.input}
                  type="date"
                  name="contractStart"
                  value={formData.contractStart}
                  onChange={handleStartChange}
                  required
                />
              </div>
              <div className={style.formGroup}>
                <label>Fin del contrato *</label>
                <input
                  className={style.input}
                  type="date"
                  name="contractEnd"
                  value={formData.contractEnd}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className={style.formGroup}>
                <label>Tipo de ajuste</label>
                <select
                  className={style.input}
                  name="adjustmentType"
                  value={formData.adjustmentType}
                  onChange={handleInputChange}
                >
                  <option value="ICL">ICL</option>
                  <option value="IPC">IPC</option>
                  <option value="porcentual">Porcentual</option>
                  <option value="manual">Manual</option>
                  <option value="sin-ajuste">Sin ajuste</option>
                </select>
              </div>
              <div className={style.formGroup}>
                <label>Frecuencia de ajuste (meses)</label>
                <input
                  className={style.input}
                  type="number"
                  name="adjustmentFrequency"
                  value={formData.adjustmentFrequency}
                  onChange={handleInputChange}
                  min="1"
                />
              </div>
              {(formData.adjustmentType === 'porcentual' ||
                formData.adjustmentType === 'manual') && (
                <div className={style.formGroup}>
                  <label>Porcentaje fijo</label>
                  <input
                    className={style.input}
                    type="number"
                    name="adjustmentPercentage"
                    value={formData.adjustmentPercentage}
                    onChange={handleInputChange}
                    step="0.01"
                  />
                </div>
              )}
            </div>

            <button
              type="button"
              className={style.collapsibleToggle}
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? '− Ocultar' : '+ Mostrar'} opciones avanzadas
            </button>

            {showAdvanced && (
              <div className={style.section}>
                <h4 className={style.sectionTitle}>Expensas y servicios</h4>
                <div className={style.formGrid}>
                  <div className={style.formGroup}>
                    <label className={style.checkboxLabel}>
                      <input
                        type="checkbox"
                        name="expensesIncluded"
                        checked={formData.expensesIncluded}
                        onChange={handleInputChange}
                      />
                      Expensas incluidas en el canon
                    </label>
                  </div>
                  <div className={style.formGroup}>
                    <label>Monto de expensas</label>
                    <input
                      className={style.input}
                      type="number"
                      name="expensesAmount"
                      value={formData.expensesAmount}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>
                </div>
                <div className={style.servicesGrid}>
                  {Object.keys(formData.servicesIncluded).map((key) => (
                    <label key={key} className={style.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={formData.servicesIncluded[key]}
                        onChange={() => handleServiceChange(key)}
                      />
                      {key.toUpperCase()}
                    </label>
                  ))}
                </div>

                <h4 className={style.sectionTitle} style={{ marginTop: 16 }}>
                  Mora
                </h4>
                <div className={style.formGrid}>
                  <div className={style.formGroup}>
                    <label>Interés diario por mora (%)</label>
                    <input
                      className={style.input}
                      type="number"
                      step="0.001"
                      name="lateInterestRate"
                      value={formData.lateInterestRate}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>
                  <div className={style.formGroup}>
                    <label>Días de gracia</label>
                    <input
                      className={style.input}
                      type="number"
                      name="gracePeriodDays"
                      value={formData.gracePeriodDays}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>
                </div>

                <h4 className={style.sectionTitle} style={{ marginTop: 16 }}>
                  Referencias internas (opcional)
                </h4>
                <div className={style.formGrid}>
                  <div className={style.formGroup}>
                    <label>ID del vendedor de referencia</label>
                    <input
                      className={style.input}
                      type="number"
                      name="sellerId"
                      value={formData.sellerId}
                      onChange={handleInputChange}
                      placeholder="No impacta producción"
                    />
                  </div>
                  <div className={style.formGroup}>
                    <label>ID de oficina</label>
                    <input
                      className={style.input}
                      type="number"
                      name="officeId"
                      value={formData.officeId}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <h4 className={style.sectionTitle} style={{ marginTop: 16 }}>
                  Notas y cláusulas
                </h4>
                <div className={style.formGrid}>
                  <div className={style.formGroup} style={{ gridColumn: '1 / -1' }}>
                    <label>Notas internas</label>
                    <textarea
                      className={style.input}
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={2}
                    />
                  </div>
                  <div className={style.formGroup} style={{ gridColumn: '1 / -1' }}>
                    <label>Cláusulas especiales</label>
                    <textarea
                      className={style.input}
                      name="specialClauses"
                      value={formData.specialClauses}
                      onChange={handleInputChange}
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === 3 && (
          <div className={style.step}>
            <h3 className={style.stepTitle}>
              <FaUsers /> Inquilinos
            </h3>
            {tenants.map((tenant, idx) => (
              <div key={idx} className={style.tenantCard}>
                <div className={style.tenantCardHeader}>
                  <span className={style.tenantCardTitle}>
                    Inquilino #{idx + 1}{' '}
                    {tenant.tenantType === 'main'
                      ? '(Principal)'
                      : tenant.tenantType === 'guarantor'
                      ? '(Garante)'
                      : tenant.tenantType === 'co-tenant'
                      ? '(Co-inquilino)'
                      : ''}
                  </span>
                  {tenants.length > 1 && (
                    <button
                      type="button"
                      className={style.removeTenantBtn}
                      onClick={() => removeTenant(idx)}
                    >
                      Quitar
                    </button>
                  )}
                </div>
                <div className={style.formGrid}>
                  <div className={style.formGroup}>
                    <label>Nombre completo *</label>
                    <input
                      className={style.input}
                      type="text"
                      value={tenant.fullName}
                      onChange={(e) => handleTenantChange(idx, 'fullName', e.target.value)}
                      required
                    />
                  </div>
                  <div className={style.formGroup}>
                    <label>DNI *</label>
                    <input
                      className={style.input}
                      type="text"
                      value={tenant.dni}
                      onChange={(e) => handleTenantChange(idx, 'dni', e.target.value)}
                      required
                    />
                  </div>
                  <div className={style.formGroup}>
                    <label>Tipo</label>
                    <select
                      className={style.input}
                      value={tenant.tenantType}
                      onChange={(e) => handleTenantChange(idx, 'tenantType', e.target.value)}
                    >
                      <option value="main">Principal</option>
                      <option value="co-tenant">Co-inquilino</option>
                      <option value="guarantor">Garante</option>
                    </select>
                  </div>
                  <div className={style.formGroup}>
                    <label>CUIT</label>
                    <input
                      className={style.input}
                      type="text"
                      value={tenant.cuit}
                      onChange={(e) => handleTenantChange(idx, 'cuit', e.target.value)}
                    />
                  </div>
                  <div className={style.formGroup}>
                    <label>Email</label>
                    <input
                      className={style.input}
                      type="email"
                      value={tenant.email}
                      onChange={(e) => handleTenantChange(idx, 'email', e.target.value)}
                    />
                  </div>
                  <div className={style.formGroup}>
                    <label>Teléfono</label>
                    <input
                      className={style.input}
                      type="text"
                      value={tenant.phone}
                      onChange={(e) => handleTenantChange(idx, 'phone', e.target.value)}
                    />
                  </div>
                  <div className={style.formGroup}>
                    <label>Ocupación</label>
                    <input
                      className={style.input}
                      type="text"
                      value={tenant.occupation}
                      onChange={(e) => handleTenantChange(idx, 'occupation', e.target.value)}
                    />
                  </div>
                  <div className={style.formGroup}>
                    <label>Empleador</label>
                    <input
                      className={style.input}
                      type="text"
                      value={tenant.employer}
                      onChange={(e) => handleTenantChange(idx, 'employer', e.target.value)}
                    />
                  </div>
                  <div className={style.formGroup}>
                    <label>Ingreso mensual</label>
                    <input
                      className={style.input}
                      type="number"
                      min="0"
                      value={tenant.monthlyIncome}
                      onChange={(e) =>
                        handleTenantChange(idx, 'monthlyIncome', e.target.value)
                      }
                    />
                  </div>
                  <div className={style.formGroup} style={{ gridColumn: '1 / -1' }}>
                    <label>Dirección anterior</label>
                    <input
                      className={style.input}
                      type="text"
                      value={tenant.previousAddress}
                      onChange={(e) =>
                        handleTenantChange(idx, 'previousAddress', e.target.value)
                      }
                    />
                  </div>
                  <div className={style.formGroup} style={{ gridColumn: '1 / -1' }}>
                    <label>Notas</label>
                    <textarea
                      className={style.input}
                      rows={2}
                      value={tenant.notes}
                      onChange={(e) => handleTenantChange(idx, 'notes', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}
            <button type="button" className={style.addTenantBtn} onClick={addTenant}>
              <FaPlus style={{ marginRight: 6 }} /> Agregar inquilino
            </button>
          </div>
        )}

        <div className={style.formActions}>
          {currentStep > 1 && (
            <button
              type="button"
              className={`${style.button} ${style.secondary}`}
              onClick={handlePrev}
            >
              Anterior
            </button>
          )}
          {currentStep < 3 && (
            <button
              type="button"
              className={`${style.button} ${style.primary}`}
              onClick={handleNext}
            >
              Siguiente
            </button>
          )}
          {currentStep === 3 && (
            <button
              type="submit"
              className={`${style.button} ${style.primary}`}
              disabled={adminRentalsLoading}
            >
              {adminRentalsLoading ? 'Creando...' : 'Crear administración'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default NewAdminRentalContract;
